import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { applyBrandingCSS, removeBrandingCSS, updateFavicon } from '../utils/branding';
import { useTheme } from '@/hooks';

/**
 * BrandingProvider component that applies branding settings to the entire platform
 * - Updates CSS variables dynamically
 * - Updates favicon
 * - Reacts to theme changes (light/dark)
 */
export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const branding = useSelector((state: RootState) => state.branding);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  useEffect(() => {
    if (branding.isLoaded) {
      // Apply CSS variables for branding colors
      applyBrandingCSS(branding.primaryColor, branding.secondaryColor, isDark);
    } else {
      // Clear out explicitly injected CSS variables to allow index.css defaults to cascade perfectly
      removeBrandingCSS();
    }
  }, [branding.primaryColor, branding.secondaryColor, branding.isLoaded, isDark]);

  useEffect(() => {
    // Always remove existing favicon links first to ensure clean state
    const existingLinks = document.querySelectorAll("link[rel*='icon']");
    existingLinks.forEach((link) => link.remove());

    if (branding.faviconUrl) {
      // Update to custom favicon
      const link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/x-icon';
      link.href = branding.faviconUrl;
      document.head.appendChild(link);
    } else {
      // Restore default favicon when it's null/empty
      // Use a timestamp query param to force browser to reload
      const defaultFavicon = document.createElement('link');
      defaultFavicon.rel = 'icon';
      defaultFavicon.type = 'image/svg+xml';
      defaultFavicon.href = `/favicon.ico?v=${Date.now()}`;
      document.head.appendChild(defaultFavicon);
    }
  }, [branding.faviconUrl, branding.isLoaded]);

  return <>{children}</>;
}
